import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';

/* ─── Design tokens ─────────────────────────────────────────────────────── */
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600&family=Syne:wght@600;700;800&display=swap');

    :root {
      --bg:        #f7f8fa;
      --bg2:       #ffffff;
      --border:    rgba(0,0,0,0.08);
      --border2:   rgba(0,0,0,0.12);
      --dim:       #8a9099;
      --body:      #3d4451;
      --ink:       #0f1117;
      --accent:    #0066ff;
      --accent-bg: rgba(0,102,255,0.06);
      --accent-bd: rgba(0,102,255,0.18);
      --success:   #10b981;
      --error:     #ef4444;
      --warning:   #f59e0b;
    }

    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: var(--bg); }

    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
    @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }

    .hd-card { animation: fadeIn 0.3s ease; }
    .hd-input {
      font-family: 'IBM Plex Mono', monospace;
      font-size: 13px;
      width: 100%;
      padding: 9px 12px;
      background: var(--bg);
      border: 1px solid var(--border2);
      border-radius: 4px;
      color: var(--ink);
      outline: none;
      transition: border-color 0.15s;
    }
    .hd-input:focus { border-color: var(--accent); }
    .hd-input:disabled { opacity: 0.5; cursor: not-allowed; }
    .hd-btn {
      font-family: 'IBM Plex Mono', monospace;
      font-size: 11px;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      padding: 9px 20px;
      border-radius: 4px;
      border: none;
      cursor: pointer;
      transition: opacity 0.15s, transform 0.1s;
    }
    .hd-btn:hover:not(:disabled) { opacity: 0.85; transform: translateY(-1px); }
    .hd-btn:active:not(:disabled) { transform: translateY(0); }
    .hd-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .hd-btn-primary { background: var(--accent); color: #fff; }
    .hd-btn-ghost {
      background: transparent;
      color: var(--accent);
      border: 1px solid var(--accent-bd);
    }
    .hd-tab {
      font-family: 'IBM Plex Mono', monospace;
      font-size: 11px;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      padding: 10px 20px;
      background: transparent;
      border: none;
      border-bottom: 2px solid transparent;
      color: var(--dim);
      cursor: pointer;
      transition: color 0.15s, border-color 0.15s;
      white-space: nowrap;
    }
    .hd-tab:hover { color: var(--ink); }
    .hd-tab-active { color: var(--accent) !important; border-bottom-color: var(--accent) !important; }

    .sub-row:hover td { background: var(--accent-bg) !important; }

    @media (max-width: 600px) {
      .sub-table th:nth-child(n+4), .sub-table td:nth-child(n+4) { display: none; }
    }
  `}</style>
);

/* ─── Nav ────────────────────────────────────────────────────────────────── */
function Nav() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 32px', height: 56,
      background: scrolled ? 'rgba(247,248,250,0.95)' : 'rgba(247,248,250,0.7)',
      backdropFilter: 'blur(16px)',
      borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
      transition: 'border-color 0.3s, background 0.3s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/')}>
        <img src="/logo/scifi_logo_transprent.png" alt="sciFi" style={{ height: 38, width: 'auto' }} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={() => navigate('/hackathon/leaderboard')}
          className="hd-btn hd-btn-ghost"
        >
          Leaderboard
        </button>
        {user && (
          <button
            type="button"
            onClick={() => { logout(); navigate('/hackathon'); }}
            style={{
              fontFamily: 'IBM Plex Mono,monospace', fontSize: 11,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '7px 16px', background: 'transparent',
              color: 'var(--dim)', border: '1px solid var(--border2)',
              borderRadius: 4, cursor: 'pointer',
            }}
          >
            Sign out
          </button>
        )}
      </div>
    </nav>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
      <div style={{
        width: 28, height: 28,
        border: '3px solid var(--border2)', borderTopColor: 'var(--accent)',
        borderRadius: '50%', animation: 'spin 0.8s linear infinite',
      }} />
    </div>
  );
}

function Alert({ type, children }) {
  const colors = {
    success: { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.3)', color: '#059669' },
    error:   { bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.3)',  color: '#dc2626' },
    info:    { bg: 'var(--accent-bg)',       border: 'var(--accent-bd)',     color: 'var(--accent)' },
  };
  const c = colors[type] || colors.info;
  return (
    <div style={{
      padding: '10px 14px',
      background: c.bg, border: `1px solid ${c.border}`,
      borderRadius: 4,
      fontFamily: 'IBM Plex Mono,monospace', fontSize: 12, color: c.color,
    }}>
      {children}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="hd-card" style={{
      background: 'var(--bg2)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      overflow: 'hidden',
      marginBottom: 24,
    }}>
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg)',
      }}>
        <span style={{
          fontFamily: 'IBM Plex Mono,monospace',
          fontSize: 10, letterSpacing: '0.15em',
          textTransform: 'uppercase', color: 'var(--dim)', fontWeight: 600,
        }}>
          {title}
        </span>
      </div>
      <div style={{ padding: 24 }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        display: 'block',
        fontFamily: 'IBM Plex Mono,monospace', fontSize: 10,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        color: 'var(--dim)', marginBottom: 6,
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function fmtScore(v) {
  return v != null ? (v).toFixed(2) + '%' : '—';
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

function StatusBadge({ status }) {
  const map = {
    ok:      { bg: 'rgba(16,185,129,0.1)',  color: '#059669',  label: 'Scored' },
    pending: { bg: 'rgba(245,158,11,0.1)',  color: '#d97706',  label: 'Pending' },
    error:   { bg: 'rgba(239,68,68,0.1)',   color: '#dc2626',  label: 'Error' },
  };
  const s = map[status] || { bg: 'var(--accent-bg)', color: 'var(--dim)', label: status };
  return (
    <span style={{
      fontFamily: 'IBM Plex Mono,monospace', fontSize: 9,
      letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
      padding: '3px 8px', borderRadius: 3,
      background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
}

function RankBadge({ rank }) {
  if (rank === 1) return <span style={{ fontSize: 20 }}>🥇</span>;
  if (rank === 2) return <span style={{ fontSize: 20 }}>🥈</span>;
  if (rank === 3) return <span style={{ fontSize: 20 }}>🥉</span>;
  return (
    <span style={{
      fontFamily: 'IBM Plex Mono,monospace', fontWeight: 700,
      fontSize: 22, color: 'var(--accent)',
    }}>
      #{rank}
    </span>
  );
}

/* ─── Profile Tab ─────────────────────────────────────────────────────────── */
function ProfileTab({ user, refreshUser }) {
  const [fullName, setFullName]     = useState(user?.full_name || '');
  const [phone, setPhone]           = useState(user?.phone_number || '');
  const [saving, setSaving]         = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  const [oldPass, setOldPass]       = useState('');
  const [newPass, setNewPass]       = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [pwSaving, setPwSaving]     = useState(false);
  const [pwMsg, setPwMsg]           = useState(null);

  async function handleProfileSave(e) {
    e.preventDefault();
    setSaving(true);
    setProfileMsg(null);
    try {
      await axios.put(`${API_BASE_URL}/api/user/profile`, {
        full_name: fullName,
        phone_number: phone,
      });
      await refreshUser();
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to update profile.' });
    } finally {
      setSaving(false);
    }
  }

  async function handlePasswordChange(e) {
    e.preventDefault();
    setPwMsg(null);
    if (newPass !== confirmPass) {
      setPwMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (newPass.length < 6) {
      setPwMsg({ type: 'error', text: 'Password must be at least 6 characters.' });
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/api/user/change-password`, {
        old_password: oldPass,
        new_password: newPass,
      });
      setOldPass(''); setNewPass(''); setConfirmPass('');
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to change password.' });
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
      {/* Basic Info */}
      <SectionCard title="Basic Information">
        <form onSubmit={handleProfileSave}>
          <Field label="Email">
            <input className="hd-input" value={user?.email || ''} disabled />
          </Field>
          <Field label="Full Name">
            <input
              className="hd-input"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder="Your full name"
            />
          </Field>
          <Field label="Phone Number">
            <input
              className="hd-input"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
            />
          </Field>
          {profileMsg && (
            <div style={{ marginBottom: 14 }}>
              <Alert type={profileMsg.type}>{profileMsg.text}</Alert>
            </div>
          )}
          <button type="submit" className="hd-btn hd-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </form>
      </SectionCard>

      {/* Change Password */}
      <SectionCard title="Change Password">
        <form onSubmit={handlePasswordChange}>
          <Field label="Current Password">
            <input
              className="hd-input"
              type="password"
              value={oldPass}
              onChange={e => setOldPass(e.target.value)}
              placeholder="••••••••"
              required
            />
          </Field>
          <Field label="New Password">
            <input
              className="hd-input"
              type="password"
              value={newPass}
              onChange={e => setNewPass(e.target.value)}
              placeholder="••••••••"
              required
            />
          </Field>
          <Field label="Confirm New Password">
            <input
              className="hd-input"
              type="password"
              value={confirmPass}
              onChange={e => setConfirmPass(e.target.value)}
              placeholder="••••••••"
              required
            />
          </Field>
          {pwMsg && (
            <div style={{ marginBottom: 14 }}>
              <Alert type={pwMsg.type}>{pwMsg.text}</Alert>
            </div>
          )}
          <button type="submit" className="hd-btn hd-btn-primary" disabled={pwSaving}>
            {pwSaving ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </SectionCard>
    </div>
  );
}

/* ─── Ranking Tab ─────────────────────────────────────────────────────────── */
function RankingTab() {
  const navigate = useNavigate();
  const [entry, setEntry]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/hackathon/my-rank`)
      .then(res => { setEntry(res.data); setError(null); })
      .catch(() => setError('Failed to load your rank.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  if (error) {
    return (
      <SectionCard title="Your Ranking">
        <Alert type="error">{error}</Alert>
      </SectionCard>
    );
  }

  if (!entry) {
    return (
      <SectionCard title="Your Ranking">
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏁</div>
          <p style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 13, color: 'var(--dim)', marginBottom: 20 }}>
            No scored submissions yet.
          </p>
          <button
            className="hd-btn hd-btn-primary"
            onClick={() => navigate('/hackathon')}
          >
            Submit your solution
          </button>
        </div>
      </SectionCard>
    );
  }

  const metrics = [
    { label: 'Composite Score', value: fmtScore(entry.composite_score), highlight: true },
    { label: 'ROC-AUC (40%)',   value: fmtScore(entry.roc_auc) },
    { label: 'PR-AUC (30%)',    value: fmtScore(entry.pr_auc) },
    { label: 'F1-Score (15%)',  value: fmtScore(entry.f1) },
    { label: 'Recall (15%)',    value: fmtScore(entry.recall) },
  ];

  return (
    <>
      {/* Rank hero */}
      <div className="hd-card" style={{
        background: 'var(--bg2)',
        border: '1px solid var(--border)',
        borderRadius: 8, marginBottom: 24,
        padding: '36px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{
            width: 80, height: 80, borderRadius: 12,
            background: 'var(--accent-bg)',
            border: '2px solid var(--accent-bd)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <RankBadge rank={entry.rank} />
          </div>
          <div>
            <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 36, color: 'var(--ink)', lineHeight: 1 }}>
              Rank #{entry.rank}
            </div>
            <div style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 12, color: 'var(--dim)', marginTop: 6 }}>
              {entry.display_name}
            </div>
            <div style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 11, color: 'var(--dim)', marginTop: 4 }}>
              Best submission · {fmtTime(entry.submitted_at)}
            </div>
          </div>
        </div>
        <button
          className="hd-btn hd-btn-ghost"
          onClick={() => navigate('/hackathon/leaderboard')}
        >
          View Full Leaderboard →
        </button>
      </div>

      {/* Score breakdown */}
      <SectionCard title="Score Breakdown">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
          {metrics.map(m => (
            <div key={m.label} style={{
              padding: '18px 20px',
              background: m.highlight ? 'var(--accent-bg)' : 'var(--bg)',
              border: `1px solid ${m.highlight ? 'var(--accent-bd)' : 'var(--border)'}`,
              borderRadius: 6,
              textAlign: 'center',
            }}>
              <div style={{
                fontFamily: 'Syne,sans-serif', fontWeight: 800,
                fontSize: m.highlight ? 28 : 22,
                color: m.highlight ? 'var(--accent)' : 'var(--ink)',
                marginBottom: 6,
              }}>
                {m.value}
              </div>
              <div style={{
                fontFamily: 'IBM Plex Mono,monospace', fontSize: 9,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                color: 'var(--dim)',
              }}>
                {m.label}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </>
  );
}

/* ─── Submissions Tab ────────────────────────────────────────────────────── */
function SubmissionsTab() {
  const navigate = useNavigate();
  const [subs, setSubs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const fetchSubs = useCallback(() => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/hackathon/my-submissions`)
      .then(res => { setSubs(res.data.submissions || []); setError(null); })
      .catch(() => setError('Failed to load submissions.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const hasPending = subs.some(s => s.score_status === 'pending');

  // Auto-refresh if any submissions are pending
  useEffect(() => {
    if (!hasPending) return;
    const id = setInterval(fetchSubs, 8000);
    return () => clearInterval(id);
  }, [hasPending, fetchSubs]);

  if (loading && subs.length === 0) return <Spinner />;

  return (
    <SectionCard title={`My Submissions${subs.length > 0 ? ` (${subs.length})` : ''}`}>
      {error && <Alert type="error">{error}</Alert>}

      {subs.length === 0 && !error ? (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
          <p style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 13, color: 'var(--dim)', marginBottom: 20 }}>
            No submissions yet.
          </p>
          <button
            className="hd-btn hd-btn-primary"
            onClick={() => navigate('/hackathon')}
          >
            Submit your solution
          </button>
        </div>
      ) : (
        <>
          {hasPending && (
            <div style={{ marginBottom: 16 }}>
              <Alert type="info">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%', background: '#0066ff',
                    display: 'inline-block', animation: 'pulse 1.6s ease-in-out infinite',
                  }} />
                  Scoring in progress — refreshing automatically every 8s
                </span>
              </Alert>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table className="sub-table" style={{
              width: '100%', borderCollapse: 'collapse',
              fontFamily: 'IBM Plex Mono,monospace',
            }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border2)', background: 'var(--bg)' }}>
                  {[
                    { label: '#',             align: 'center', w: 40 },
                    { label: 'File',          align: 'left' },
                    { label: 'Status',        align: 'center', w: 90 },
                    { label: 'Score',         align: 'center', w: 96 },
                    { label: 'ROC-AUC',       align: 'center', w: 88 },
                    { label: 'PR-AUC',        align: 'center', w: 80 },
                    { label: 'F1',            align: 'center', w: 70 },
                    { label: 'Recall',        align: 'center', w: 76 },
                    { label: 'Submitted',     align: 'center', w: 110 },
                  ].map(h => (
                    <th key={h.label} style={{
                      padding: '10px 14px', textAlign: h.align,
                      color: 'var(--dim)', fontWeight: 600,
                      letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 9,
                      whiteSpace: 'nowrap',
                      ...(h.w ? { width: h.w, minWidth: h.w } : {}),
                    }}>
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subs.map((sub, idx) => (
                  <tr
                    key={sub.id}
                    className="sub-row"
                    style={{ borderBottom: '1px solid var(--border)' }}
                  >
                    <td style={{ padding: '12px 14px', textAlign: 'center', color: 'var(--dim)', fontSize: 11 }}>
                      {subs.length - idx}
                    </td>
                    <td style={{
                      padding: '12px 14px', color: 'var(--ink)', fontWeight: 500,
                      fontSize: 12, maxWidth: 220,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={sub.original_filename}>
                      {sub.original_filename}
                      {sub.score_error && (
                        <div style={{ fontSize: 10, color: 'var(--error)', marginTop: 2, whiteSpace: 'normal' }}>
                          {sub.score_error}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                      <StatusBadge status={sub.score_status} />
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 700, fontSize: 12, color: 'var(--accent)' }}>
                      {fmtScore(sub.composite_score)}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: 12, color: 'var(--body)' }}>
                      {fmtScore(sub.roc_auc)}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: 12, color: 'var(--body)' }}>
                      {fmtScore(sub.pr_auc)}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: 12, color: 'var(--body)' }}>
                      {fmtScore(sub.f1)}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: 12, color: 'var(--body)' }}>
                      {fmtScore(sub.recall)}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: 11, color: 'var(--dim)', whiteSpace: 'nowrap' }}>
                      {fmtTime(sub.submitted_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{
            marginTop: 16,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: 8,
          }}>
            <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 11, color: 'var(--dim)' }}>
              {subs.length} submission{subs.length !== 1 ? 's' : ''} total
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="hd-btn hd-btn-ghost" onClick={fetchSubs} disabled={loading}>
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
              <button className="hd-btn hd-btn-primary" onClick={() => navigate('/hackathon')}>
                New Submission
              </button>
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
}

/* ─── Main Dashboard ─────────────────────────────────────────────────────── */
const TABS = [
  { id: 'ranking',     label: 'My Ranking' },
  { id: 'submissions', label: 'Submissions' },
  { id: 'profile',     label: 'Profile' },
];

export default function HackathonDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('ranking');

  // Redirect unauthenticated users
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { state: { from: location } });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <>
        <GlobalStyle />
        <Spinner />
      </>
    );
  }

  if (!user) return null;

  return (
    <>
      <GlobalStyle />
      <Nav />

      <main style={{
        minHeight: '100vh', background: 'var(--bg)',
        paddingTop: 80, paddingBottom: 80,
      }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: '0 24px' }}>

          {/* Page header */}
          <div style={{ paddingTop: 32, marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 20, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
              <span style={{
                fontFamily: 'IBM Plex Mono,monospace', fontSize: 10,
                letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--accent)',
              }}>
                sciFi Hackathon 2026
              </span>
              <div style={{ width: 20, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
            </div>

            <div style={{
              display: 'flex', alignItems: 'flex-end',
              justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
            }}>
              <div>
                <h1 style={{
                  fontFamily: 'Syne,sans-serif', fontWeight: 800,
                  fontSize: 'clamp(28px, 5vw, 48px)', color: 'var(--ink)',
                  letterSpacing: '-0.03em', lineHeight: 1.1,
                }}>
                  My Dashboard
                </h1>
                <p style={{
                  fontFamily: 'IBM Plex Mono,monospace', fontSize: 12,
                  color: 'var(--dim)', marginTop: 6,
                }}>
                  {user.full_name || user.email}
                </p>
              </div>
              <button
                className="hd-btn hd-btn-ghost"
                onClick={() => navigate('/hackathon')}
              >
                ← Hackathon
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div style={{
            display: 'flex', gap: 0,
            borderBottom: '1px solid var(--border)',
            marginBottom: 28, overflowX: 'auto',
          }}>
            {TABS.map(t => (
              <button
                key={t.id}
                className={`hd-tab${activeTab === t.id ? ' hd-tab-active' : ''}`}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {activeTab === 'ranking'     && <RankingTab />}
          {activeTab === 'submissions' && <SubmissionsTab />}
          {activeTab === 'profile'     && <ProfileTab user={user} refreshUser={refreshUser} />}

        </div>
      </main>
    </>
  );
}
